import { memo, useContext } from 'react';
import cn from 'classnames';
import { useAtom } from 'jotai';
import { reactFlowInstanceAtom, branchUpdaterKey } from '@src/atoms';
import { ReactflowInstanceContext } from '@src/scenario-editor/ScenarioEditor';
import type {
  NodeToRenderProps,
  NodeToRenderData,
  CustomIfConditionNodeData,
  CustomIfConditionBehaviorNodeData,
  CustomNode,
  CustomMessageNodeData,
  RulesCondition, ConditionStatement,
} from './types/custom-nodes.type';
import type { SelectboxOptionItem } from '@src/scenario-editor/render-utils/types';
import { applyIfBranchHocs } from '@src/scenario-editor/utils';
import { CustomTooltip } from '@src/components/tooltip/Tooltip';

export const MenuItem = ({ children, className, ...props }: NodeToRenderProps) => {
  return (
  <div
    className={
      cn([
        "flex rounded-md p-2 border-2 border-transparent bg-white h-full justify-center items-center w-fit text-sm truncate",
        className,
      ])
    }
    {...props}
  >{children}</div>
)};

export const NodeItem = (props: NodeToRenderProps) => {
  const [updaterKey] = useAtom(branchUpdaterKey);
  const dependsOn = (props.data as CustomIfConditionNodeData).dependsOn;
  const reactFlowInstance = useContext(ReactflowInstanceContext).get(reactFlowInstanceAtom)!;
  const nodes = reactFlowInstance?.getNodes() as CustomNode[];
  const conditionNode = nodes?.find(node => node.id === dependsOn);
  const condSourceId = (conditionNode?.data as CustomIfConditionNodeData)?.condSourceId;
  const condSourceNode = nodes?.find(node => node.id === condSourceId);
  const condSourceNodeResponse = (condSourceNode?.data as CustomMessageNodeData)?.response;

  if (!reactFlowInstance) {
    return null;
  }

  const isLegacyBranching = !!(conditionNode?.data as CustomIfConditionNodeData)?.condStatements
    && !(conditionNode?.data as CustomIfConditionBehaviorNodeData)?.conditions;
  let condStatements;

  if (isLegacyBranching) {
     condStatements = (conditionNode?.data as CustomIfConditionNodeData)?.condStatements ?? [];
  } else {
     condStatements = (conditionNode?.data as CustomIfConditionBehaviorNodeData)?.conditions ?? [];
  }

  const branchIndex = Number(props?.customNodeData?.branchId.split('-').slice(-1)) - 1;
  let currentCondStatement;
  let currentCondStatementLabel;

  if (isLegacyBranching) {
    currentCondStatement = condStatements[branchIndex] as ConditionStatement;
    currentCondStatementLabel =
      currentCondStatement?.op === 'or'
        ? (currentCondStatement?.value ?? [])
          .map(statementChunk =>
            condSourceNodeResponse.find((resp: SelectboxOptionItem) => resp.value === statementChunk.value)?.label ?? statementChunk.value)
        : ['Else'];
  } else {
    currentCondStatement = condStatements[branchIndex] as RulesCondition;
    currentCondStatementLabel = currentCondStatement?.rules
      ? [`Branch ${branchIndex + 1}`]
      : ['Else'];
  }
  const escapedLabel = currentCondStatementLabel.length
    ? currentCondStatementLabel
    : [`Option ${branchIndex + 1}`];

  return (
    <div
      key={updaterKey + props.id}
      data-testid={`node=>${(props.data as NodeToRenderData).nodeType}`}
      className={
        cn([
          props.className,
          "flex flex-col py-2 px-2 rounded-md border-2 border-transparent justify-center items-center text-sm cursor-auto w-full",
        ])
      }
      style={props.style}
    >
      <div
        className={cn([
          "bg-input py-2 px-3 rounded-md w-full",
          {
          'border-2': props.selected,
          '!border-blue-400': props.selected,
          }
        ])}
      >
        <CustomTooltip content={escapedLabel.join(' or ')}>
          <p className="text-center text-sm truncate max-w-full break-all">
            {escapedLabel.map((label, idx) => (
              <>
                <span key={label}>{label}</span>
                {idx < escapedLabel.length - 1 && <span className="text-muted-foreground"> or </span>}
              </>
            ))}
          </p>
        </CustomTooltip>
      </div>
    </div>
  )
};

const WrappedNodeToRender = applyIfBranchHocs(NodeItem);

const MessageInfo: React.FC<NodeToRenderProps> = (props: NodeToRenderProps) => {
  const { title } = props.data as NodeToRenderData;
  return (
    <WrappedNodeToRender key={props.id} {...props}>{title}</WrappedNodeToRender>
  );
};

export const IfBranchNode = memo(MessageInfo);
